import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserProfile,
  userProfilesApi,
} from '@texas/api/endpoints/userProfilesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { saveBlobAsFile } from '@texas/utils/helpers/filesHelper';
import { userProfileProjectColumns } from './UserProfileProjectColumns';

interface Props {
  profile: UserProfile;
  onClose: () => void;
}

const pageSize = 25;

export function UserProfileProjectsModal({ profile, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const { data, refetch, loading, error } = useApiResource(
    userProfilesApi.getProjectAssignments,
  );
  const { request: exportRequest, loading: exporting } = useApiRequest(
    userProfilesApi.exportProjectAssignments,
  );
  const columns = useMemo(
    () => userProfileProjectColumns(t, onClose),
    [t, onClose],
  );

  useEffect(() => {
    refetch(profile.userId, profile.id, page, pageSize);
  }, [profile.userId, profile.id, page, refetch]);

  const exportProjects = async () => {
    const response = await exportRequest(profile.userId, profile.id);
    if (!response.hasError) {
      saveBlobAsFile(
        response.data,
        `Project-assignments-user-${profile.userId}-profile-${
          profile.id
        }-${new Date().toISOString().substring(0, 10)}.xlsx`,
      );
    } else {
      toast({
        title: response.error.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      variant="entityDirectory"
      size="6xl"
      scrollBehavior="inside"
      isOpen={true}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {t('userDetails.profileProjectAssignments', { name: profile.name })}
        </ModalHeader>
        <ModalBody>
          <Flex
            align="center"
            justify="space-between"
            gap={4}
            mb={4}
            wrap="wrap"
          >
            <Text color="entityDirectory.mutedText" fontSize="sm">
              {t('userDetails.connectedProjects', {
                count: data?.totalItems ?? profile.connectedProjectsCount,
              })}
            </Text>
            <Button
              variant="entityDirectorySecondary"
              size="sm"
              leftIcon={<Icons.FileExport boxSize={defaultIconSize} />}
              isDisabled={loading || !!error || !data?.totalItems}
              isLoading={exporting}
              onClick={exportProjects}
            >
              {t('userDetails.exportAssignments')}
            </Button>
          </Flex>
          <DataTableContainer
            error={error}
            datatable={{
              columns,
              data: data?.items ?? [],
              isLoading: loading,
              variant: 'entity-directory',
              rowProps: { getRowId: (project) => String(project.id) },
            }}
            pagination={{
              pageSize,
              currentPage: page,
              totalItems: data?.totalItems ?? 0,
              onPageChange: setPage,
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="entityDirectorySecondary" onClick={onClose}>
            {t('general.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
